import React, { useCallback, useEffect, useState } from 'react';
import { faFlask, faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, Badge, Row, Col, Form, Button, Spinner } from 'react-bootstrap-5';
import pluralize from 'pluralize';

import type { PropertyCrmSync, RethinkList } from 'bundles/connect/types';
import Select from 'react-select';
import OutOfCreditsAlert from '../out_of_credits_alert';
import { fetchRethinkLists, enrichRethinkList } from 'bundles/connect/helpers/prospect_api';
import { debounce } from 'lodash';

type EnrichListModalStartProps = {
  loadingCredits: boolean;
  listPropertySizeMap?: { id: string; size: number }[];
  modalStage: number;
  setModalStage: (stage: number) => void;
  remainingCredits: number;
  sendEmail: boolean;
  setListUrl: (url: string) => void;
  setSendEmail: (sendEmail: boolean) => void;
  setRethinkSync: (sync: PropertyCrmSync) => void;
}

export default function EnrichListModalStart({
  loadingCredits,
  listPropertySizeMap,
  modalStage,
  setModalStage,
  remainingCredits,
  sendEmail,
  setListUrl,
  setSendEmail,
  setRethinkSync
}: EnrichListModalStartProps) {
  const [loading, setLoading] = useState(false);
  const [selectedList, setSelectedList] = useState<RethinkList | undefined>(undefined);
  const [rethinkLists, setRethinkLists] = useState<RethinkList[]>([]);
  const [searchText, setSearchText] = useState('');

  const debouncedSearch = useCallback(debounce((search) => {
    fetchRethinkLists({ name: search }).then(res => setRethinkLists(res));
  }, 300), []);

  const handleSubmit = () => {
    setLoading(true);
    enrichRethinkList({
      include_contacts: true,
      rethink_list_id: selectedList?.id,
      send_finished_email: sendEmail,
      rethink_list_name_override: selectedList?.name
    }).then((res) => {
      setModalStage(2);
      setSelectedList(undefined);
      setSendEmail(false);
      setSearchText('');
      setRethinkLists([]);
      setRethinkSync(res);
      setListUrl(res.listUrl);
    }).finally(() => {
      setLoading(false);
    });

  };

  useEffect(() => {
    if (modalStage > 0) debouncedSearch(searchText);
  }, [modalStage, searchText]);

  const notEnoughCredits = remainingCredits >= 0 &&
    remainingCredits < (listPropertySizeMap?.find(map => map.id === selectedList?.id)?.size || 0);

  return (
    <>
      <Modal.Header className="flex-row" closeButton>
        <div className="d-flex flex-column w-100">
          <Badge bg="" className="text-body fw-normal rounded muted me-auto" style={{ fontSize: '0.875rem' }}>
            <FontAwesomeIcon className="me-1 text-secondary" icon={faFlask} />
            BETA
          </Badge>
          <Modal.Title as="h5">Enrich List</Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Row className="pb-4 pt-1 gx-2">
          <Col xs={8}>
            Select a list below to enrich contact data on each property within.
          </Col>
          <Col xs={4}>
            <Badge bg="" bsPrefix="badge mh-100" className="py-2 px-3 text-muted fw-normal rounded muted">
              <span className="fs-x-small me-2">
                {pluralize('Credit', remainingCredits)} Remaining
              </span>
              <strong className="fs-large">
                {loadingCredits ? <Spinner animation="border" size="sm" /> : remainingCredits}
              </strong>
            </Badge>
          </Col>
        </Row>
        <Row className="pt-3">
          <Col>
            <Form.Label htmlFor="rethink-list-search">List Name</Form.Label>
            <Select
              className='as-button active pb-4'
              isClearable
              menuPlacement='auto'
              options={rethinkLists}
              getOptionLabel={(list) => `${list.name} (${listPropertySizeMap ?
                (listPropertySizeMap.find((map) => map.id === list.id)?.size || 0) :
                '0'} Properties)`}
              getOptionValue={(list) => list.id}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              value={selectedList}
              onChange={(selectedOption) => setSelectedList(selectedOption as RethinkList)}
              onInputChange={setSearchText}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Check
              id='email-me-enrichment-complete'
              checked={sendEmail}
              label="Email me when enrichment is complete"
              onChange={(e) => setSendEmail(e.target.checked)}
            />
          </Col>
        </Row>
        {notEnoughCredits && (
          <Row>
            <Col>
              <OutOfCreditsAlert enrichment />
            </Col>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="tertiary" onClick={() => setModalStage(0)}>
          Cancel
        </Button>
        <Button disabled={notEnoughCredits || !selectedList || loading} onClick={() => handleSubmit()}>
          <FontAwesomeIcon icon={faWandMagicSparkles} />
          Begin Enrichment
          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </Button>
      </Modal.Footer>
    </>
  )
}
